import React, { useState, useMemo, useEffect, useRef } from "react"
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import { Head, router } from "@inertiajs/react"
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Plus, MoreVertical, Edit, Trash2, Users, X, ChevronRight, ChevronDown, Building2, Search } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { useFilterStorage } from "@/hooks/use-filter-storage"

const defaultFilters = {
    searchTerm: "",
    statusFilter: "All Statuses",
};

export default function Departments({ departments }) {
    const { filters, updateFilter, resetFilters } = useFilterStorage('departments-filters', defaultFilters);

    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [editingDepartment, setEditingDepartment] = useState(null)
    const [deletingDepartment, setDeletingDepartment] = useState(null)
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        status: "active",
        parent_id: null,
        settings: {
            icon_color: "blue"
        }
    })
    const [expandedDepartments, setExpandedDepartments] = useState(new Set())
    const { toast } = useToast()

    // Handle URL department parameter from global search (open modal for specific department)
    const urlDeptApplied = useRef(false);
    useEffect(() => {
        if (urlDeptApplied.current) return;
        
        const urlParams = new URLSearchParams(window.location.search);
        const deptId = urlParams.get('department');
        
        if (deptId) {
            urlDeptApplied.current = true;
            const dept = departments.find(d => d.id === parseInt(deptId));
            if (dept) {
                // Open edit modal for this department
                setEditingDepartment(dept);
                setFormData({
                    name: dept.name,
                    description: dept.description || "",
                    status: dept.status,
                    parent_id: dept.parent_id,
                    settings: dept.settings || { icon_color: "blue" }
                });
                setIsEditOpen(true);
            }
        }
        
        // Handle ?action=new URL parameter to open create form from quick actions menu
        if (urlParams.get('action') === 'new') {
            setEditingDepartment(null);
            setFormData({
                name: "",
                description: "",
                status: "active",
                parent_id: null,
                settings: {
                    icon_color: "blue"
                }
            });
            setIsCreateOpen(true);
            // Clean up URL
            router.replace(window.location.pathname, { preserveState: true, preserveScroll: true });
        }
    }, [departments]);

    // Organize departments into hierarchical structure
    const organizeDepartments = (departments) => {
        const departmentMap = new Map()
        const rootDepartments = []

        // First pass: create map of all departments
        departments.forEach(dept => {
            departmentMap.set(dept.id, { ...dept, children: [] })
        })

        // Second pass: build hierarchy
        departments.forEach(dept => {
            if (dept.parent_id && departmentMap.has(dept.parent_id)) {
                departmentMap.get(dept.parent_id).children.push(departmentMap.get(dept.id))
            } else {
                rootDepartments.push(departmentMap.get(dept.id))
            }
        })

        return rootDepartments
    }

    // Filter departments based on search and status
    const filteredDepartments = useMemo(() => {
        const filtered = departments.filter((department) => {
            const matchesSearch = department.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                                department.description?.toLowerCase().includes(filters.searchTerm.toLowerCase())
            const matchesStatus = filters.statusFilter === "All Statuses" || 
                                department.status.toLowerCase() === filters.statusFilter.toLowerCase()
            return matchesSearch && matchesStatus
        })
        
        return organizeDepartments(filtered)
    }, [departments, filters.searchTerm, filters.statusFilter])

    // Flatten departments for counting
    const flattenedDepartments = useMemo(() => {
        const flatten = (depts) => {
            let result = []
            depts.forEach(dept => {
                result.push(dept)
                if (dept.children && dept.children.length > 0) {
                    result = result.concat(flatten(dept.children))
                }
            })
            return result
        }
        return flatten(filteredDepartments)
    }, [filteredDepartments])

    const handleCreate = (e) => {
        e.preventDefault()
        
        router.post("/departments", formData, {
            onSuccess: () => {
                setIsCreateOpen(false)
                setFormData({ name: "", description: "", status: "active", parent_id: null, settings: { icon_color: "blue" } })
                toast({
                    title: "Success",
                    description: "Department created successfully.",
                })
            },
            onError: (errors) => {
                toast({
                    title: "Error",
                    description: "Failed to create department.",
                    variant: "destructive",
                })
            },
        })
    }

    const handleRowClick = (department) => {
        setEditingDepartment(department)
        setFormData({
            name: department.name,
            description: department.description || "",
            status: department.status,
            parent_id: department.parent_id,
            settings: department.settings || { icon_color: "blue" }
        })
        setIsEditOpen(true)
    }

    const handleEdit = (department) => {
        setEditingDepartment(department)
        setFormData({
            name: department.name,
            description: department.description || "",
            status: department.status,
            parent_id: department.parent_id,
            settings: department.settings || { icon_color: "blue" }
        })
        setIsEditOpen(true)
    }

    const handleUpdate = (e) => {
        e.preventDefault()
        
        router.put(`/departments/${editingDepartment.id}`, formData, {
            onSuccess: () => {
                setIsEditOpen(false)
                setEditingDepartment(null)
                setFormData({ name: "", description: "", status: "active", parent_id: null, settings: { icon_color: "blue" } })
                toast({
                    title: "Success",
                    description: "Department updated successfully.",
                })
            },
            onError: (errors) => {
                toast({
                    title: "Error",
                    description: "Failed to update department.",
                    variant: "destructive",
                })
            },
        })
    }

    const handleDeleteClick = (department) => {
        setDeletingDepartment(department)
        setIsDeleteOpen(true)
    }

    const handleDelete = () => {
        router.delete(`/departments/${deletingDepartment.id}`, {
            onSuccess: () => {
                setIsDeleteOpen(false)
                setDeletingDepartment(null)
                toast({
                    title: "Success",
                    description: "Department deleted successfully.",
                })
            },
            onError: (errors) => {
                toast({
                    title: "Error",
                    description: "Failed to delete department. It may have users assigned to it.",
                    variant: "destructive",
                })
            },
        })
    }

    const clearFilters = () => {
        resetFilters()
    }

    const hasActiveFilters = filters.searchTerm !== "" || filters.statusFilter !== "All Statuses"

    const toggleExpanded = (departmentId) => {
        setExpandedDepartments(prev => {
            const newSet = new Set(prev)
            if (newSet.has(departmentId)) {
                newSet.delete(departmentId)
            } else {
                newSet.add(departmentId)
            }
            return newSet
        })
    }

    // DepartmentRow component for rendering individual departments
    const DepartmentRow = ({ department, depth = 0 }) => {
        const isExpanded = expandedDepartments.has(department.id)
        const hasChildren = department.children && department.children.length > 0

        return (
            <>
                <TableRow 
                    key={department.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(department)}
                >
                    <TableCell className="font-medium">
                        {department.id}
                    </TableCell>
                    <TableCell>
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                                {hasChildren && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            toggleExpanded(department.id)
                                        }}
                                    >
                                        {isExpanded ? (
                                            <ChevronDown className="h-4 w-4" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4" />
                                        )}
                                    </Button>
                                )}
                                {!hasChildren && <div className="w-6" />}
                                <div 
                                    className="flex-shrink-0"
                                    style={{ marginLeft: `${depth * 20}px` }}
                                >
                                    <div 
                                        className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                            department.settings?.icon_color === 'blue' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                                            department.settings?.icon_color === 'green' ? 'bg-gradient-to-r from-green-400 to-green-600' :
                                            department.settings?.icon_color === 'red' ? 'bg-gradient-to-r from-red-400 to-red-600' :
                                            department.settings?.icon_color === 'yellow' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                                            department.settings?.icon_color === 'purple' ? 'bg-gradient-to-r from-purple-400 to-purple-600' :
                                            department.settings?.icon_color === 'pink' ? 'bg-gradient-to-r from-pink-400 to-pink-600' :
                                            department.settings?.icon_color === 'indigo' ? 'bg-gradient-to-r from-indigo-400 to-indigo-600' :
                                            department.settings?.icon_color === 'orange' ? 'bg-gradient-to-r from-orange-400 to-orange-600' :
                                            department.settings?.icon_color === 'teal' ? 'bg-gradient-to-r from-teal-400 to-teal-600' :
                                            department.settings?.icon_color === 'cyan' ? 'bg-gradient-to-r from-cyan-400 to-cyan-600' :
                                            'bg-gradient-to-r from-blue-400 to-blue-600'
                                        }`}
                                    >
                                        <span className="text-white text-sm font-medium">
                                            {department.name.charAt(0)}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <div className="font-medium text-sm">
                                        {department.name}
                                    </div>
                                    {department.parent && (
                                        <div className="text-xs text-muted-foreground">
                                            {department.parent.name}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                        <div className="truncate text-sm text-muted-foreground">
                            {department.description || "—"}
                        </div>
                    </TableCell>
                    <TableCell>
                        <Badge
                            variant={
                                department.status === "active"
                                    ? "default"
                                    : "secondary"
                            }
                            className={
                                department.status === "active"
                                    ? "bg-green-100 text-green-800 hover:bg-green-100"
                                    : ""
                            }
                        >
                            {department.status === "active" ? "Active" : "Inactive"}
                        </Badge>
                    </TableCell>
                    <TableCell>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="flex items-center text-sm text-muted-foreground cursor-help">
                                        <Users className="h-4 w-4 mr-1" />
                                        {department.total_users_count || 0}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                    <div className="space-y-1">
                                        {department.all_users && department.all_users.length > 0 ? (
                                            <div className="space-y-1 max-h-32 overflow-y-auto">
                                                {department.all_users.map((user) => (
                                                    <div key={user.id} className="text-xs text-muted-foreground">
                                                        {user.name}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-muted-foreground">
                                                No users assigned
                                            </div>
                                        )}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </TableCell>
                    <TableCell>
                        <div className="flex items-center text-sm text-muted-foreground">
                            <Building2 className="h-4 w-4 mr-1" />
                            {department.children_count || 0}
                        </div>
                    </TableCell>
                    <TableCell>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setFormData({
                                            name: "",
                                            description: "",
                                            status: "active",
                                            parent_id: department.id,
                                            settings: { icon_color: "blue" }
                                        })
                                        setIsCreateOpen(true)
                                    }}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Sub-Dept
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleEdit(department)
                                    }}
                                >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit Department
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteClick(department)
                                    }}
                                    className="text-destructive focus:text-destructive"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Department
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </TableCell>
                </TableRow>
                {hasChildren && isExpanded && department.children.map(child => (
                    <DepartmentRow key={child.id} department={child} depth={depth + 1} />
                ))}
            </>
        )
    }

    return (
        <AuthenticatedLayout>
            <Head title="DME | Departments" />
            
            <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold tracking-tight">Departments</h1>
                    <p className="text-muted-foreground">
                        Manage departments and organize your team structure
                    </p>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-3">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                                placeholder="Search departments..."
                                value={filters.searchTerm}
                                onChange={(e) => updateFilter('searchTerm', e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        
                        <Select value={filters.statusFilter} onValueChange={(value) => updateFilter('statusFilter', value)}>
                            <SelectTrigger className="w-[140px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All Statuses">All Statuses</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        {hasActiveFilters && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearFilters}
                                className="h-9 px-3"
                            >
                                <X className="h-4 w-4 mr-1" />
                                Reset
                            </Button>
                        )}
                    </div>
                </div>

                {/* Results count */}
                <div className="text-sm text-muted-foreground">
                    {flattenedDepartments.length} {flattenedDepartments.length === 1 ? 'department' : 'departments'} found
                </div>

                <Card>
                    <CardContent className="p-0">
                        {filteredDepartments.length === 0 ? (
                            <div className="text-center py-12">
                                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg font-medium mb-2">
                                    {departments.length === 0 ? "No departments yet" : "No departments found"}
                                </h3>
                                <p className="text-muted-foreground mb-4">
                                    {departments.length === 0 
                                        ? "Get started by creating your first department."
                                        : "Try adjusting your search or filter criteria."
                                    }
                                </p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead className="w-12">ID</TableHead>
                                        <TableHead>Department</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Users</TableHead>
                                        <TableHead>Sub-Departments</TableHead>
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredDepartments.map((department) => (
                                        <DepartmentRow key={department.id} department={department} />
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Create Dialog */}
                <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                    <DialogContent className="w-10/12 sm:w-full">
                        <DialogHeader>
                            <DialogTitle>Create Department</DialogTitle>
                            <DialogDescription>
                                Add a new department to organize your team.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreate}>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                        placeholder="e.g. Engineering, Sales, Marketing"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) =>
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                        placeholder="Department description (optional)"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="parent">Parent Department</Label>
                                    <Select
                                        value={formData.parent_id ? formData.parent_id.toString() : "none"}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, parent_id: value === "none" ? null : parseInt(value) })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select parent department (optional)" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Parent (Root Department)</SelectItem>
                                            {departments
                                                .filter(dept => {
                                                    // Only filter out the current department when editing
                                                    if (editingDepartment) {
                                                        return dept.id !== editingDepartment.id
                                                    }
                                                    return true
                                                })
                                                .map((department) => (
                                                    <SelectItem key={department.id} value={department.id.toString()}>
                                                        {department.name}
                                                    </SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="icon_color">Icon Color</Label>
                                    <Select
                                        value={formData.settings.icon_color}
                                        onValueChange={(value) =>
                                            setFormData({ 
                                                ...formData, 
                                                settings: { ...formData.settings, icon_color: value }
                                            })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="blue">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                                                    <span>Blue</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="green">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-400 to-green-600"></div>
                                                    <span>Green</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="red">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-red-400 to-red-600"></div>
                                                    <span>Red</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="yellow">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600"></div>
                                                    <span>Yellow</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="purple">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-400 to-purple-600"></div>
                                                    <span>Purple</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="pink">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-pink-400 to-pink-600"></div>
                                                    <span>Pink</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="indigo">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-indigo-400 to-indigo-600"></div>
                                                    <span>Indigo</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="orange">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-orange-400 to-orange-600"></div>
                                                    <span>Orange</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="teal">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-teal-400 to-teal-600"></div>
                                                    <span>Teal</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="cyan">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600"></div>
                                                    <span>Cyan</span>
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    <Select
                                        value={formData.status}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, status: value })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="inactive">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="dark:text-white">Create Department</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Edit Dialog */}
                <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                    <DialogContent className="w-10/12 sm:w-full">
                        <DialogHeader>
                            <DialogTitle>Edit Department</DialogTitle>
                            <DialogDescription>
                                Update the department information.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleUpdate}>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-name">Name</Label>
                                    <Input
                                        id="edit-name"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                        placeholder="Department name"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-description">Description</Label>
                                    <Textarea
                                        id="edit-description"
                                        value={formData.description}
                                        onChange={(e) =>
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                        placeholder="Department description (optional)"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-parent">Parent Department</Label>
                                    <Select
                                        value={formData.parent_id ? formData.parent_id.toString() : "none"}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, parent_id: value === "none" ? null : parseInt(value) })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select parent department (optional)" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Parent (Root Department)</SelectItem>
                                            {departments
                                                .filter(dept => dept.id !== editingDepartment?.id)
                                                .map((department) => (
                                                    <SelectItem key={department.id} value={department.id.toString()}>
                                                        {department.name}
                                                    </SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-icon_color">Icon Color</Label>
                                    <Select
                                        value={formData.settings.icon_color}
                                        onValueChange={(value) =>
                                            setFormData({ 
                                                ...formData, 
                                                settings: { ...formData.settings, icon_color: value }
                                            })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="blue">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                                                    <span>Blue</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="green">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-400 to-green-600"></div>
                                                    <span>Green</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="red">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-red-400 to-red-600"></div>
                                                    <span>Red</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="yellow">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600"></div>
                                                    <span>Yellow</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="purple">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-400 to-purple-600"></div>
                                                    <span>Purple</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="pink">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-pink-400 to-pink-600"></div>
                                                    <span>Pink</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="indigo">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-indigo-400 to-indigo-600"></div>
                                                    <span>Indigo</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="orange">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-orange-400 to-orange-600"></div>
                                                    <span>Orange</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="teal">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-teal-400 to-teal-600"></div>
                                                    <span>Teal</span>
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="cyan">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600"></div>
                                                    <span>Cyan</span>
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-status">Status</Label>
                                    <Select
                                        value={formData.status}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, status: value })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="inactive">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="dark:text-white">Update Department</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete Department</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to delete "{deletingDepartment?.name}"? 
                                This action cannot be undone. Users in this department 
                                will be unassigned.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDelete}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </AuthenticatedLayout>
    )
} 